import { collection, doc, limit, onSnapshot, orderBy, query, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { normalizeEmail } from '../utils/rolesUsuario';

/*
 * Roles del personal interno (ver docs/roles.md).
 *
 * - `rolesUsuarios/{email}`: el rol que le asignó un admin. Soft delete con
 *   active:false (vuelve a valer el SGC o la elección de transición).
 * - `accesosUsuarios/{email}`: último ingreso y rol con que entró cada uno, para
 *   que el admin vea quién usa la planilla sin rol asignado.
 * - `rolesUsuariosHistorial`: una línea por cambio, que escribe la Cloud Function
 *   onRolUsuarioWritten (solo lectura acá).
 *
 * Las escrituras van con merge: así un cambio de rol no borra la delegación
 * (`asigna`), que solo cambia el dueño y que las reglas exigen intacta.
 *
 * Los dos usan como id el email en minúsculas, que es lo que comparan las reglas.
 */
const ROLES_COLLECTION = 'rolesUsuarios';
const ACCESOS_COLLECTION = 'accesosUsuarios';
const HISTORIAL_COLLECTION = 'rolesUsuariosHistorial';
const HISTORIAL_LIMITE = 40;

const EMAIL_AXON_PATTERN = /^[a-z0-9._%+-]+@axoncapital\.com\.ar$/;

export const isEmailAxonValido = (email) => EMAIL_AXON_PATTERN.test(normalizeEmail(email));

const toDocData = (snap) => (snap.exists() ? { id: snap.id, ...snap.data() } : null);

/** Rol asignado al usuario logueado. Emite null si no tiene. */
export const subscribeRolUsuario = (email, onData, onError) => {
  const id = normalizeEmail(email);
  if (!id) {
    onData(null);
    return () => {};
  }
  return onSnapshot(
    doc(db, ROLES_COLLECTION, id),
    (snap) => onData(toDocData(snap)),
    (error) => {
      console.error('rolesUsuariosService.subscribeRolUsuario failed:', { id, error });
      if (onError) onError(error);
    },
  );
};

const subscribeCollection = (name, onData, onError) => onSnapshot(
  collection(db, name),
  (snapshot) => onData(snapshot.docs.map(toDocData).filter(Boolean)),
  (error) => {
    console.error(`rolesUsuariosService: error suscribiendo ${name}:`, error);
    if (onError) onError(error);
  },
);

/** Todos los roles asignados (solo admin). */
export const subscribeRolesUsuarios = (onData, onError) =>
  subscribeCollection(ROLES_COLLECTION, onData, onError);

/** Últimos ingresos del personal interno (admins y delegados). */
export const subscribeAccesosUsuarios = (onData, onError) =>
  subscribeCollection(ACCESOS_COLLECTION, onData, onError);

/** Últimos cambios de roles, del más reciente al más viejo (admins y delegados). */
export const subscribeHistorialRoles = (onData, onError) => onSnapshot(
  query(collection(db, HISTORIAL_COLLECTION), orderBy('at', 'desc'), limit(HISTORIAL_LIMITE)),
  (snapshot) => onData(snapshot.docs.map(toDocData).filter(Boolean)),
  (error) => {
    console.error('rolesUsuariosService.subscribeHistorialRoles failed:', error);
    if (onError) onError(error);
  },
);

const firmaAdmin = (admin) => ({
  asignadoPor: normalizeEmail(admin.email),
  asignadoPorNombre: admin.displayName || '',
  updatedAt: serverTimestamp(),
});

/** Asigna (o cambia) el rol de un usuario @axoncapital. */
export const asignarRolUsuario = async ({ admin, email, rol, nombre = '' }) => {
  const id = normalizeEmail(email);
  if (!isEmailAxonValido(id)) {
    throw new Error('El email debe ser una cuenta @axoncapital.com.ar.');
  }
  try {
    await setDoc(doc(db, ROLES_COLLECTION, id), {
      email: id,
      ...(nombre ? { nombre } : {}),
      rol,
      active: true,
      ...firmaAdmin(admin),
    }, { merge: true });
  } catch (error) {
    console.error('rolesUsuariosService.asignarRolUsuario failed:', { id, rol, error });
    throw error;
  }
};

/** Quita el rol asignado (soft delete): el usuario vuelve al SGC o a la transición. */
export const quitarRolUsuario = async ({ admin, rolUsuario }) => {
  try {
    await setDoc(doc(db, ROLES_COLLECTION, rolUsuario.id), {
      email: rolUsuario.id,
      rol: rolUsuario.rol,
      active: false,
      ...firmaAdmin(admin),
    }, { merge: true });
  } catch (error) {
    console.error('rolesUsuariosService.quitarRolUsuario failed:', { id: rolUsuario?.id, error });
    throw error;
  }
};

/**
 * Cambia los roles que esa persona puede asignar (solo el dueño; las reglas lo
 * validan). Requiere que tenga un rol asignado.
 */
export const actualizarDelegacion = async ({ admin, rolUsuario, asigna }) => {
  try {
    await setDoc(doc(db, ROLES_COLLECTION, rolUsuario.id), {
      email: rolUsuario.id,
      rol: rolUsuario.rol,
      active: rolUsuario.active === true,
      asigna,
      ...firmaAdmin(admin),
    }, { merge: true });
  } catch (error) {
    console.error('rolesUsuariosService.actualizarDelegacion failed:', { id: rolUsuario?.id, error });
    throw error;
  }
};

/** Registra el ingreso del usuario logueado con el rol que está usando. */
export const registrarAccesoUsuario = (user, rolUsado) => {
  const id = normalizeEmail(user?.email);
  if (!id || !rolUsado) return Promise.resolve();
  return setDoc(doc(db, ACCESOS_COLLECTION, id), {
    email: id,
    nombre: user.displayName || '',
    rolUsado,
    ultimoLoginAt: serverTimestamp(),
  }).catch((error) => {
    console.error('rolesUsuariosService.registrarAccesoUsuario failed:', { id, error });
  });
};
